const TZ = 'Europe/Istanbul';

export const FINANCE_PRESET = {
  BUGUN: 'bugun',
  BU_HAFTA: 'bu_hafta',
  BU_AY: 'bu_ay',
  DUN: 'dun',
  GECEN_HAFTA: 'gecen_hafta',
  GECEN_AY: 'gecen_ay',
  BU_YIL: 'bu_yil',
  OZEL: 'ozel'
};

export function istanbulDateKey(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

function splitKey(ymd) {
  const [y, m, d] = String(ymd || '').split('-').map((n) => Number(n));
  return { y, m, d };
}

function padKey(y, m, d) {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function addCivilDays(ymd, days) {
  const { y, m, d } = splitKey(ymd);
  const dt = new Date(Date.UTC(y, m - 1, d + Number(days || 0)));
  return padKey(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

export function civilWeekdaySun0(ymd) {
  const { y, m, d } = splitKey(ymd);
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).getUTCDay();
}

export function mondayOf(ymd) {
  const sun0 = civilWeekdaySun0(ymd);
  const sinceMon = (sun0 + 6) % 7;
  return addCivilDays(ymd, -sinceMon);
}

export function istanbulStartUtc(ymd) {
  return new Date(`${ymd}T00:00:00+03:00`);
}

export function istanbulEndUtc(ymd) {
  return new Date(`${ymd}T23:59:59.999+03:00`);
}

export function daysInMonth(y, m) {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function inclusiveDayCount(startYmd, endYmd) {
  const a = splitKey(startYmd);
  const b = splitKey(endYmd);
  if (!a.y || !b.y) return 0;
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000) + 1;
}

/** Aylık giderin seçilen güne / aralığa düşen payı. 30.000 ₺ kira, 30 günlük ay, 1 gün → 1.000 ₺. */
export function allocateMonthlyAmount(monthlyTry, startYmd, endYmd) {
  const amount = Number(monthlyTry);
  if (!Number.isFinite(amount) || amount === 0) return 0;
  if (!startYmd || !endYmd) return Math.round(amount * 100) / 100;
  const start = startYmd <= endYmd ? startYmd : endYmd;
  const end = startYmd <= endYmd ? endYmd : startYmd;
  let total = 0;
  let cursor = start;
  while (cursor <= end) {
    const { y, m } = splitKey(cursor);
    const monthLast = padKey(y, m, daysInMonth(y, m));
    const sliceEnd = monthLast < end ? monthLast : end;
    const days = inclusiveDayCount(cursor, sliceEnd);
    const dim = daysInMonth(y, m) || 1;
    total += amount * (days / dim);
    cursor = addCivilDays(sliceEnd, 1);
  }
  return Math.round(total * 100) / 100;
}

export function resolvePresetRange(preset, { now = new Date(), startYmd, endYmd } = {}) {
  const today = istanbulDateKey(now);
  const { y, m } = splitKey(today);

  if (preset === FINANCE_PRESET.DUN) {
    const key = addCivilDays(today, -1);
    return { preset, startYmd: key, endYmd: key, start: istanbulStartUtc(key), end: istanbulEndUtc(key) };
  }
  if (preset === FINANCE_PRESET.BU_HAFTA) {
    const startKey = mondayOf(today);
    const endKey = addCivilDays(startKey, 6);
    return { preset, startYmd: startKey, endYmd: endKey, start: istanbulStartUtc(startKey), end: istanbulEndUtc(endKey) };
  }
  if (preset === FINANCE_PRESET.GECEN_HAFTA) {
    const thisMon = mondayOf(today);
    const startKey = addCivilDays(thisMon, -7);
    const endKey = addCivilDays(startKey, 6);
    return { preset, startYmd: startKey, endYmd: endKey, start: istanbulStartUtc(startKey), end: istanbulEndUtc(endKey) };
  }
  if (preset === FINANCE_PRESET.BU_AY) {
    const startKey = padKey(y, m, 1);
    const endKey = padKey(y, m, daysInMonth(y, m));
    return { preset, startYmd: startKey, endYmd: endKey, start: istanbulStartUtc(startKey), end: istanbulEndUtc(endKey) };
  }
  if (preset === FINANCE_PRESET.GECEN_AY) {
    const py = m === 1 ? y - 1 : y;
    const pm = m === 1 ? 12 : m - 1;
    const startKey = padKey(py, pm, 1);
    const endKey = padKey(py, pm, daysInMonth(py, pm));
    return { preset, startYmd: startKey, endYmd: endKey, start: istanbulStartUtc(startKey), end: istanbulEndUtc(endKey) };
  }
  if (preset === FINANCE_PRESET.BU_YIL) {
    const startKey = padKey(y, 1, 1);
    const endKey = padKey(y, 12, 31);
    return { preset, startYmd: startKey, endYmd: endKey, start: istanbulStartUtc(startKey), end: istanbulEndUtc(endKey) };
  }
  if (preset === FINANCE_PRESET.OZEL) {
    const a = startYmd || today;
    const b = endYmd || today;
    const startKey = a <= b ? a : b;
    const endKey = a <= b ? b : a;
    return { preset, startYmd: startKey, endYmd: endKey, start: istanbulStartUtc(startKey), end: istanbulEndUtc(endKey) };
  }
  return { preset: FINANCE_PRESET.BUGUN, startYmd: today, endYmd: today, start: istanbulStartUtc(today), end: istanbulEndUtc(today) };
}

export function comparePreset(preset) {
  if (preset === FINANCE_PRESET.BUGUN) return FINANCE_PRESET.DUN;
  if (preset === FINANCE_PRESET.BU_HAFTA) return FINANCE_PRESET.GECEN_HAFTA;
  if (preset === FINANCE_PRESET.BU_AY) return FINANCE_PRESET.GECEN_AY;
  if (preset === FINANCE_PRESET.BU_YIL) return 'gecen_yil';
  return null;
}

export function resolveCompareRange(preset, range, { now = new Date() } = {}) {
  const mapped = comparePreset(preset);
  if (mapped === FINANCE_PRESET.DUN || mapped === FINANCE_PRESET.GECEN_HAFTA || mapped === FINANCE_PRESET.GECEN_AY) {
    return resolvePresetRange(mapped, { now });
  }
  if (mapped === 'gecen_yil') {
    const today = istanbulDateKey(now);
    const y = splitKey(today).y - 1;
    const startKey = padKey(y, 1, 1);
    const endKey = padKey(y, 12, 31);
    return { preset: 'gecen_yil', startYmd: startKey, endYmd: endKey, start: istanbulStartUtc(startKey), end: istanbulEndUtc(endKey) };
  }
  if (preset === FINANCE_PRESET.OZEL && range?.startYmd && range?.endYmd) {
    const a = splitKey(range.startYmd);
    const b = splitKey(range.endYmd);
    const span = Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000) + 1;
    const prevEnd = addCivilDays(range.startYmd, -1);
    const prevStart = addCivilDays(prevEnd, -(span - 1));
    return { preset: 'onceki_aralik', startYmd: prevStart, endYmd: prevEnd, start: istanbulStartUtc(prevStart), end: istanbulEndUtc(prevEnd) };
  }
  return null;
}

export function formatPeriodLabel(range) {
  if (!range?.startYmd) return '';
  if (range.startYmd === range.endYmd) {
    const dt = istanbulStartUtc(range.startYmd);
    return dt.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: TZ });
  }
  const a = istanbulStartUtc(range.startYmd).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', timeZone: TZ });
  const b = istanbulStartUtc(range.endYmd).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ });
  return `${a} – ${b}`;
}

export function islemToDate(islem) {
  const t = islem?.timestamp;
  if (!t) return null;
  if (typeof t.toDate === 'function') return t.toDate();
  if (t instanceof Date) return t;
  if (typeof t === 'number') return new Date(t);
  if (typeof t === 'string') {
    const parsed = new Date(t);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof t.seconds === 'number') return new Date(t.seconds * 1000);
  return null;
}

export function islemDateKey(islem) {
  const d = islemToDate(islem);
  return d ? istanbulDateKey(d) : null;
}

/** @deprecated local-tz helpers kept only if old imports linger */
export function toDateKey(date) {
  return istanbulDateKey(date);
}

export function startOfDay(date) {
  return istanbulStartUtc(istanbulDateKey(date));
}

export function endOfDay(date) {
  return istanbulEndUtc(istanbulDateKey(date));
}

export const FINANCE_PERIOD = {
  GUN: FINANCE_PRESET.BUGUN,
  HAFTA: FINANCE_PRESET.BU_HAFTA,
  AY: FINANCE_PRESET.BU_AY
};
